// src/components/stages/PharmacyStage.tsx
// Dispense whatever this visit's consultation prescribed, before the bill and
// closure — adapted from PharmacyModule's DispensingTab (same FEFO-first
// picking and Schedule H1/X prescriber gate) but scoped to this one visit's
// patient/consultation rather than "any of today's approved prescriptions."
// Only reached for OP visits — an IP-bound visit skips straight past this
// (see backend/routes/visits.js), since an admitted patient's dispensing
// runs continuously through the stay instead.
import { useState, useEffect, useRef, type CSSProperties } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useBillingSettings } from '../../hooks/useBillingSettings';
import { getInventory, createPharmacyBill, prescriptions as prescriptionsApi } from '../../api';
import { keys, branchKey } from '../../lib/queryKeys';
import type { StageProps } from '../../types/visit';
import { StageLayout } from '../ui/StageLayout';
import { StageActionBar } from '../ui/StageActionBar';
// The banner already carries the "reviewed / none known / not reviewed"
// allergy badge for every stage it mounts in (see PatientBanner.tsx) — that
// closed the assessment's P1 finding here already. What this stage was
// still missing is the *drug-level* check: allergy/duplicate-therapy
// warnings computed against the actual cart, the same live call
// DispensingTab.tsx (the "already most modern" sibling module the
// assessment points to) already makes before checkout.
import SafetyAlertBanner from '../SafetyAlertBanner';
import { useDispensingSafetyCheck } from '../../features/pharmacy/useDispensingSafetyCheck';
import { SubstituteFlow } from '../../features/pharmacy/SubstitutePicker';
import {
  cartTotals, needsPrescriberFor, resolveGstPercent, reconcileGstOptions,
  unresolvedQuantityOverages as findUnresolvedQuantityOverages,
  unresolvedOffPrescription as findUnresolvedOffPrescription,
  offPrescriptionMayBeMissedSubstitution,
  applyReasonToLines,
} from '../../features/pharmacy/dispensingCart';
import { useDispensingSettings } from '../../features/pharmacy/useNearExpiry';
import { NearExpiryBadge, LaterBatchPanel, batchSelectionSummary } from '../../features/pharmacy/BatchSelectionPanel';
import { pickFefoBatch, unresolvedLaterBatches, applyBatchSelectionReason, isNearExpiry } from '../../features/pharmacy/batchSelection';

/** One inventory batch, as returned by GET /api/pharmacy/inventory —
 * deliberately a local shape rather than reusing features/pharmacy/types.ts's
 * InventoryItem, since this screen also reads `active` (the medicine-master
 * flag search-and-add filters on) which that type doesn't model. */
interface InventoryRow {
  id: number;
  medicine_id: number;
  generic_name: string;
  brand_name: string | null;
  strength: string | null;
  quantity_available: number;
  expiry_date: string;
  mrp: string | number;
  gst_percent: string | number;
  schedule: string;
  batch_number: string;
  active?: boolean;
}

/** One line of this visit's structured prescription (prescription_items),
 * as returned nested under GET /api/prescriptions/:id. */
interface RxItem {
  id: number;
  medicine_id: number | null;
  drug_name: string;
  strength: string | null;
  quantity: number | string | null;
  dose_quantity: number | string | null;
  dose_unit: string | null;
  route: string | null;
  frequency_code: string | null;
  frequency_text: string | null;
  duration_value: number | string | null;
  duration_unit: string | null;
  instructions: string | null;
  /** Decorated server-side across every PRIOR bill on this visit or dispensed
   * separately before this stage — see utils/prescriptionDispenseStatus.js.
   * null = no derivable cap (PRN/SOS). */
  remaining_quantity: number | null;
}

interface Rx {
  id: number;
  status: string;
  prescriber_name?: string | null;
  items: RxItem[];
}

interface CartItem {
  inventory_id: number;
  medicine_id: number;
  name: string;
  qty: number;
  mrp: number;
  gst: number;
  schedule: string;
  batch: string;
  expiry: string;
  available: number;
  prescription_item_id?: number;
  /** Required by the backend when this line's drug identity doesn't match
   * what was prescribed on prescription_item_id — otherwise the sale is
   * hard-blocked. Set only via the 🔀 Substitute picker on an unfulfillable
   * prescribed line (stockFor never auto-resolves a different drug — see
   * that function's own header — so this is never set any other way). See
   * migration_dispensing_substitution.sql and DispensingTab.tsx's identical
   * field, which this mirrors. */
  substitution_reason?: string;
  quantity_override_reason?: string;
  /** Required by the backend when this line has no prescription_item_id at
   * all — added via search/advanced-batch rather than resolved from a
   * prescribed line. See routes/billing.js's POST /pharmacy/create and
   * DispensingTab.tsx's identical field, which this mirrors. */
  off_prescription_reason?: string;
  /** Required by the backend when this line sells a later-expiry batch while
   * an earlier one could cover it — only reachable via the advanced batch
   * picker or 🔀 Substitute. See features/pharmacy/batchSelection.ts. */
  batch_selection_reason?: string;
  batch_selection_remarks?: string;
}

type PaymentMode = 'Cash' | 'UPI' | 'Card';
type PaymentStatus = 'Paid' | 'Pending';

const isExpired = (d: string | null | undefined) => !!d && new Date(d) < new Date();

const dispensableOf = (inv: InventoryRow[] | null) => (inv || []).filter(m => m.quantity_available > 0 && !isExpired(m.expiry_date));

// prescription_items.medicine_id is nullable on purpose — a doctor may
// prescribe a drug this pharmacy has never stocked, and nothing in the
// prescribing UI populates it today — so the snapshotted drug_name is the
// only key most items carry. Comparing on a normalised name keeps
// "Paracetamol 500 mg" and "paracetamol" together without letting a
// different drug through: punctuation and spacing are ignored, the name
// itself still has to match exactly.
const norm = (s: unknown) => String(s ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
// Strengths drop spacing entirely rather than collapsing it: "500 MG" and
// "500mg" are one dose written two ways. Used for both the exact-strength
// preference in stockFor and the mismatch badge below — they have to agree,
// or a batch could be picked as exact and then flagged as a mismatch.
const normStrength = (s: unknown) => norm(s).replace(/ /g, '');
const num  = (v: unknown) => (v === null || v === undefined || v === '' ? '' : String(Number(v)));

// Prefix match, not substring — searching "imo" should surface "Imol 10mg"
// but not "Domperidone", which a pharmacist scanning a 1000+ row formulary by
// brand/generic name would not expect either. Case-insensitive per the same
// rule as the rest of the file's matching.
const startsWithSearch = (name: unknown, term: string) => String(name ?? '').toLowerCase().startsWith(term);

// The batches of one prescribed line this branch actually holds.
function stockFor(item: RxItem, dispensable: InventoryRow[]): InventoryRow[] {
  let matches = item.medicine_id ? dispensable.filter(m => m.medicine_id === item.medicine_id) : [];
  if (!matches.length) {
    const name = norm(item.drug_name);
    if (!name) return [];
    matches = dispensable.filter(m => norm(m.generic_name) === name || norm(m.brand_name) === name);
  }
  // Narrow to the prescribed strength only when the stock actually has it —
  // a strength mismatch shouldn't hide the drug, it should still be offered.
  const strength = normStrength(item.strength);
  if (strength) {
    const exact = matches.filter(m => normStrength(m.strength) === strength);
    if (exact.length) return exact;
  }
  return matches;
}

// FEFO for a prescribed line: the earliest-expiring batch that covers the
// quantity to dispense, else the earliest with stock — the shared rule in
// features/pharmacy/batchSelection.ts, so this stage and DispensingTab.tsx
// pre-pick the same batch the sale engine expects.
const fefoOf = (batches: InventoryRow[], qty = 1, cart: CartItem[] = []): InventoryRow | null => pickFefoBatch(batches, qty, cart);

// stockFor prefers the prescribed strength, so a mismatch here means no batch
// of this drug carries it and the pharmacist is being offered a different one.
// Only flagged when both sides state a strength — a blank on either side is
// unknown, not a mismatch, and warning about it would train people to ignore
// the badge.
const strengthMismatch = (it: RxItem, batch: InventoryRow | null) => {
  const prescribed = normStrength(it.strength);
  const available = normStrength(batch && batch.strength);
  return !!prescribed && !!available && prescribed !== available;
};

// Same amber caution treatment as the Schedule H1/X badge already used on
// cart lines below — a warning to read, not an error that blocks anything.
const MISMATCH_BADGE: CSSProperties = {
  fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 8,
  background: '#FEF3C7', color: '#92400E', whiteSpace: 'nowrap',
};

// Doses per day for the prescribing shorthand prescription_items.frequency_code
// carries. HS ("at night") is once daily. SOS and STAT are deliberately absent:
// they describe an occasion, not a daily rate, and are handled separately
// below. frequency_text is free prose ("2 puffs before exertion") and is not
// parsed — only the code is machine-readable, by design.
const DOSES_PER_DAY: Record<string, number> = { OD: 1, BD: 2, TDS: 3, QID: 4, HS: 1 };
const DAYS_PER_UNIT: Record<string, number> = { d: 1, wk: 7, mo: 30 };

// How much to dispense for one prescribed line.
//
// prescription_items.quantity is "the total to dispense... usually derivable
// from dose x frequency x duration, but stored explicitly because the doctor
// can override it" (migration_clinical_capture.sql), so an explicit quantity
// wins — it is the prescriber's deliberate call about a full pack or a part
// course. With none set, derive that same product rather than falling back to
// a meaningless 1: dose 2 for 3 days is 6, and BD for 5 days is 10 doses.
//
// A missing frequency means once a day, and a missing duration means one day —
// both round down to the smaller supply, never a larger one. SOS/STAT have no
// derivable course at all (the schema comment says as much), so they get a
// single occasion's worth. Anything still underivable keeps the old default of
// 1, and the pharmacist can adjust every line by hand as before.
const prescribedQty = (it: RxItem): number => {
  const explicit = Math.round(parseFloat(String(it.quantity)));
  if (Number.isFinite(explicit) && explicit > 0) return explicit;

  const dose = parseFloat(String(it.dose_quantity));
  if (!Number.isFinite(dose) || dose <= 0) return 1;

  const code = String(it.frequency_code || '').trim().toUpperCase();
  if (code === 'SOS' || code === 'STAT') return Math.max(1, Math.ceil(dose));

  const durationValue = parseFloat(String(it.duration_value));
  const days = Number.isFinite(durationValue) && durationValue > 0
    ? durationValue * (DAYS_PER_UNIT[it.duration_unit || 'd'] || 1)
    : 1;

  // Ceil, not round: half a tablet short of a course is a short course.
  return Math.max(1, Math.ceil(dose * (DOSES_PER_DAY[code] || 1) * days));
};

// What this stage dispenses for a prescribed line: the prescribed course,
// capped at what is still owed on it.
const qtyToDispense = (it: RxItem): number =>
  it.remaining_quantity != null ? Math.min(prescribedQty(it), it.remaining_quantity) : prescribedQty(it);

// True only for genuinely partial stock — some of the FEFO batch is on the
// shelf, just not the whole prescribed course. dispensable already excludes
// zero-quantity batches (dispensableOf), so a null batch is the separate
// "Not stocked here" case and stays that way; this never reclassifies it.
const isPartialStock = (it: RxItem, batch: InventoryRow | null) => !!batch && batch.quantity_available > 0 && batch.quantity_available < prescribedQty(it);

// One cart row for a chosen inventory batch — lifted out of addItem so the
// dropdown, the search results and the prescription prefill produce identical
// lines.
//
// The billing settings are passed in rather than read from useBillingSettings
// because this is a module-level helper; every caller must pass them, so the
// prefilled rows and the hand-added ones can't drift apart on GST.
function lineFor(
  med: InventoryRow, qty = 1, { gstEnabled = true, gstPercentageOptions = [] as number[] } = {},
  prescriptionItemId?: number, substitutionReason?: string
): CartItem {
  const validGst = resolveGstPercent(parseFloat(String(med.gst_percent)) || 12, gstPercentageOptions);
  return {
    inventory_id: med.id, medicine_id: med.medicine_id, name: `${med.generic_name} ${med.strength || ''}`.trim(),
    qty: Math.max(1, Math.min(qty, med.quantity_available)),
    mrp: parseFloat(String(med.mrp)), gst: gstEnabled ? validGst : 0, schedule: med.schedule,
    batch: med.batch_number, expiry: med.expiry_date, available: med.quantity_available,
    // Set only for a line seeded from this visit's prescription — see
    // addItem/dispense below. Carrying this through to POST /billing/pharmacy/create
    // is what lets that endpoint auto-complete the prescription once every
    // item is fully dispensed (routes/billing.js), and what lets it enforce
    // the same prescribed-drug-identity check DispensingTab.tsx already gets.
    prescription_item_id: prescriptionItemId,
    substitution_reason: substitutionReason,
  };
}

const doseSummary = (it: RxItem) => [
  [num(it.dose_quantity), it.dose_unit].filter(Boolean).join(' '),
  it.route,
  it.frequency_text || it.frequency_code,
  it.duration_value ? `${it.duration_value}${it.duration_unit || 'd'}` : '',
  it.instructions,
].filter(Boolean).join(' · ');

// The reasons a pharmacist can give for not dispensing medicines that were
// actually available. Stored as the stable `value` (never the label) so the
// wording can be reworded later without orphaning historical rows; 'other'
// is the only one that carries free text alongside it.
//
// 'nothing_to_dispense' is deliberately NOT in this list — it isn't a choice
// anyone makes, it's the automatic case where there was never anything on
// the shelf to decline, and it never reaches this modal.
const DISPENSE_REASONS = [
  { value: 'patient_declined',           label: 'Patient declined' },
  { value: 'patient_will_collect_later', label: 'Patient will collect later' },
  { value: 'patient_purchased_elsewhere', label: 'Patient purchased elsewhere' },
  { value: 'doctor_cancelled',           label: 'Doctor cancelled prescription' },
  { value: 'insurance_payment_issue',    label: 'Insurance/payment issue' },
  { value: 'other',                      label: 'Other' },
] as const;

export default function PharmacyStage({ visit, patient, completeStage, loading }: Pick<StageProps, 'visit' | 'patient' | 'completeStage' | 'loading'>) {
  const { gstEnabled, gstPercentageOptions } = useBillingSettings();
  const { near_expiry_days: nearExpiryDays, batch_selection_reasons: batchReasons } = useDispensingSettings();
  const queryClient = useQueryClient();
  const [inventory, setInventory] = useState<InventoryRow[] | null>(null);
  const [rx, setRx] = useState<Rx | null>(null);
  const [rxLoaded, setRxLoaded] = useState(false);
  const [items, setItems] = useState<CartItem[]>([]);
  const [payMode, setPayMode] = useState<PaymentMode>('Cash');
  const [payStatus, setPayStatus] = useState<PaymentStatus>('Paid');
  const [prescriberName, setPrescriberName] = useState('');
  const [prescriberReg, setPrescriberReg] = useState('');
  const [advancedBatch, setAdvancedBatch] = useState(false);
  const [medicineSearch, setMedicineSearch] = useState('');
  /** id of the prescription_item currently showing its inline 🔀 Substitute
   * picker, if any — mirrors DispensingTab.tsx's identical field. */
  const [substitutingFor, setSubstitutingFor] = useState<number | null>(null);
  const [billing, setBilling] = useState(false);
  const [error, setError] = useState('');
  // Skip-reason prompt: only open when medicines were actually available.
  const [reasonOpen, setReasonOpen] = useState(false);
  const [reasonCode, setReasonCode] = useState('');
  const [reasonText, setReasonText] = useState('');
  // Shared free-text reason applied to every currently-over-quantity cart
  // line at once — one field for the whole cart rather than one per line,
  // matching how the H1/X prescriber name below already applies once to
  // the whole cart rather than once per H1/X line.
  const [quantityOverrideReason, setQuantityOverrideReason] = useState('');
  // Same shared-field pattern as quantityOverrideReason above, for lines
  // added via search/advanced-batch that carry no prescription_item_id at
  // all — see off_prescription_reason on CartItem.
  const [offPrescriptionReason, setOffPrescriptionReason] = useState('');

  const prefilled = useRef(false);

  // Visit dispensing sells from the 'visit' channel's store (the OP Pharmacy once split).
  useEffect(() => { getInventory('visit').then(setInventory).catch(() => setInventory([])); }, []);

  // What this visit's Consultation prescribed. Its very existence is why the
  // visit is at this stage at all (advanceStage in routes/visits.js routes
  // here only for a non-draft, item-bearing prescription), so the pharmacist
  // is shown it whether or not the branch stocks any of it.
  //
  // Two reads because the list endpoint returns headers only — items come
  // from GET /:id (HMS_V4_API_CONTRACT.md §Prescriptions). Both are already
  // branch-scoped server-side, and re-running on mount is what makes this
  // survive a page refresh.
  useEffect(() => {
    if (!visit?.id) { setRxLoaded(true); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const list: Rx[] = await prescriptionsApi.list({ visit_id: visit.id });
        const usable = (Array.isArray(list) ? list : []).filter(p => !['cancelled', 'entered-in-error'].includes(p.status));
        const chosen = usable.find(p => p.status === 'active') || usable.find(p => p.status === 'completed') || usable[0];
        const full: Rx | null = chosen ? await prescriptionsApi.get(chosen.id) : null;
        if (!cancelled) setRx(full);
      } catch {
        // A prescription we can't read must not block dispensing — the
        // dropdown and both stage exits below still work without it.
        if (!cancelled) setRx(null);
      }
      if (!cancelled) setRxLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [visit?.id]);

  const consultationId: number | null = visit?.stages?.find(s => s.stage === 'consultation')?.stage_data?.consultation_id || null;

  const dispensable = dispensableOf(inventory);
  // One row per prescribed medicine, each resolved independently against
  // stock: batch === null means this branch doesn't hold it.
  const rxLines = (rx?.items || []).map(it => {
    const batch = fefoOf(stockFor(it, dispensable), qtyToDispense(it));
    return { it, batch, mismatch: strengthMismatch(it, batch), partial: isPartialStock(it, batch) };
  });
  // Carries the flag onto the cart row being billed, so the warning is on the
  // line the pharmacist removes rather than only in the panel above it.
  const mismatchedInventoryIds = new Set(rxLines.filter(l => l.mismatch && l.batch).map(l => l.batch!.id));
  const partialStockInventoryIds = new Set(rxLines.filter(l => l.partial && l.batch).map(l => l.batch!.id));
  // remaining_quantity is decorated onto each item server-side (across every
  // PRIOR bill, on this visit or dispensed separately through the Pharmacy
  // module before this stage was even opened) — see
  // utils/prescriptionDispenseStatus.js. null means no derivable cap
  // (PRN/SOS), never flagged as an overage below.
  const remainingByRxItemId = new Map((rx?.items || []).map(it => [it.id, it.remaining_quantity]));
  // A cart line linked to a prescription item whose quantity exceeds what
  // remains on that line — the quantity counterpart of the mismatch/partial
  // badges above. See dispensingCart.ts's own header for why this rule is
  // shared with DispensingTab.tsx but the mismatch/substitution machinery
  // above (stockFor, mismatchedInventoryIds) is not.
  const unresolvedQuantityOverages = findUnresolvedQuantityOverages(items, remainingByRxItemId);
  const applyQuantityOverrideReason = () => {
    const reason = quantityOverrideReason.trim();
    if (!reason) return;
    const overIds = new Set(unresolvedQuantityOverages.map(i => i.inventory_id));
    setItems(prev => applyReasonToLines(prev, overIds, 'quantity_override_reason', reason));
    setQuantityOverrideReason('');
  };

  // A cart line with no prescription_item_id at all is a medicine added
  // outside the prescription — via search or the advanced batch picker —
  // not a substitution of a prescribed line (stockFor above only ever picks
  // a batch whose name matches the prescribed drug, so a genuinely
  // different-drug substitution never reaches this cart; only "not
  // prescribed at all" does). The backend hard-blocks this unless
  // off_prescription_reason is set — same live, resolve-before-checkout
  // pattern as unresolvedQuantityOverages above, and the same field
  // DispensingTab.tsx's identical picker already resolves.
  const unresolvedOffPrescription = findUnresolvedOffPrescription(items);
  // Advisory nudge, not a gate — see dispensingCart.ts's own header on why
  // this can happen (free-add/search name-matching missed a genuine
  // substitution) and why it's never blocking (off-prescription is a
  // legitimate, tracked path in its own right).
  const offPrescriptionMayBeSubstitution = offPrescriptionMayBeMissedSubstitution(items, rx?.items || []);
  const applyOffPrescriptionReason = () => {
    const reason = offPrescriptionReason.trim();
    if (!reason) return;
    const unlinkedIds = new Set(unresolvedOffPrescription.map(i => i.inventory_id));
    setItems(prev => applyReasonToLines(prev, unlinkedIds, 'off_prescription_reason', reason));
    setOffPrescriptionReason('');
  };

  // Seed the cart from the prescription once both it and the inventory are
  // known. Lines with no stock, or already fully dispensed elsewhere
  // (remaining_quantity === 0), are left out of the cart but still listed
  // below, so nothing invents an inventory item that doesn't exist and
  // nothing pre-seeds a quantity that would immediately need an override
  // reason before the pharmacist has done anything.
  useEffect(() => {
    if (prefilled.current || inventory === null || !rxLoaded) return;
    prefilled.current = true;
    const seeded: CartItem[] = [];
    (rx?.items || []).forEach(it => {
      if (it.remaining_quantity === 0) return;
      const qty = qtyToDispense(it);
      // `seeded` so two prescribed lines for the same drug don't both count
      // on the same units of one batch.
      const batch = fefoOf(stockFor(it, dispensableOf(inventory)), qty, seeded);
      if (!batch) return;
      seeded.push(lineFor(batch, qty, { gstEnabled, gstPercentageOptions }, it.id));
    });
    if (seeded.length) setItems(seeded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventory, rxLoaded, rx]);

  // Search-and-add offers only ACTIVE medicines — a deactivated drug (see
  // docs/HMS_v4_Drug_Master_Formulary_Architecture.md §17/§24) shouldn't be
  // handed to a pharmacist as a normal option for a brand-new dispense line,
  // mirroring the same active=true filter routes/medicines.js already
  // applies to the doctor-facing prescribing search. Deliberately scoped to
  // just this search UI, not `dispensable` itself — an existing cart line
  // auto-matched from the prescription (stockFor, above) must still resolve
  // even if the medicine has since been deactivated; only NEW-item search
  // visibility changes. Existing stock for an inactive medicine stays fully
  // sellable until it's gone.
  const searchableStock = dispensable.filter(m => m.active !== false);
  const fefoGroups: Record<number, InventoryRow> = {};
  searchableStock.forEach(m => {
    const key = m.medicine_id;
    const existing = fefoGroups[key];
    if (!existing || new Date(m.expiry_date) < new Date(existing.expiry_date)) fefoGroups[key] = m;
  });
  const totalsByMed: Record<number, number> = {};
  searchableStock.forEach(m => { totalsByMed[m.medicine_id] = (totalsByMed[m.medicine_id] || 0) + m.quantity_available; });

  // Below 3 characters nothing is shown — the threshold only kicks in once
  // it's narrow enough to be a useful results list rather than filtering on
  // every keystroke. The "Add medicine" dropdown further down stays
  // unfiltered regardless, as a fallback for browsing the full formulary.
  const searchTerm = medicineSearch.trim().toLowerCase();
  const searchActive = searchTerm.length >= 3;
  // A medicine already in the cart (auto-added from the prescription, or
  // added by hand) is excluded by medicine_id, not name — so a different
  // strength of the same drug (a different medicine_id in this schema) stays
  // searchable, matching what stockFor already treats as a different offer.
  const cartMedicineIds = new Set(items.map(i => i.medicine_id));
  const searchResults = searchActive
    ? Object.values(fefoGroups).filter(m =>
        !cartMedicineIds.has(m.medicine_id) &&
        (startsWithSearch(m.generic_name, searchTerm) || startsWithSearch(m.brand_name, searchTerm))
      )
    : [];

  // Selecting a search result goes through the exact same addItem the
  // dropdown uses — no separate dispensing/FEFO path — then resets the box,
  // which also hides the results list since searchActive depends on it.
  const pickSearchResult = (invId: number) => {
    addItem(invId, undefined, undefined, undefined, true);
    setMedicineSearch('');
  };

  const { subtotal, gstAmt, total } = cartTotals(items, gstEnabled);
  const needsPrescriber = needsPrescriberFor(items);

  // qty and prescriptionItemId are only passed by the prescription panel,
  // which knows the prescribed course and which line it's fulfilling; the
  // dropdowns/search leave both undefined (qty keeps lineFor's default of 1,
  // prescriptionItemId stays unset — a manually-added medicine isn't linked
  // to any prescribed line). substitutionReason is only passed by the 🔀
  // Substitute picker (see SubstituteFlow below).
  // `fefo` — the caller picked a MEDICINE (search), not a batch, so it is
  // re-resolved to the earliest batch covering the quantity, net of the cart.
  // The advanced picker and 🔀 Substitute keep the batch the pharmacist chose.
  const addItem = (invId: number | string, qty?: number, prescriptionItemId?: number, substitutionReason?: string, fefo = false) => {
    const picked = (inventory || []).find(m => m.id === parseInt(String(invId)));
    if (!picked) return;
    const med = fefo ? fefoOf(dispensable.filter(m => m.medicine_id === picked.medicine_id), qty ?? 1, items) ?? picked : picked;
    setItems(prev => [...prev, lineFor(med, qty, { gstEnabled, gstPercentageOptions }, prescriptionItemId, substitutionReason)]);
  };

  // Lines on a later-expiry batch while an earlier one could cover them. The
  // backend hard-blocks these unless batch_selection_reason is set — same
  // resolve-before-checkout pattern as the quantity/off-prescription banners.
  const unresolvedLater = unresolvedLaterBatches(items, dispensable);
  const applyLaterBatchReason = (ids: Set<number>, reason: string, remarks: string) =>
    setItems(prev => applyBatchSelectionReason(prev, ids, reason, remarks));
  const switchToEarlierBatch = (line: CartItem, earlierId: number) => {
    const earlier = (inventory || []).find(m => m.id === earlierId);
    if (!earlier) return;
    setItems(prev => prev.map(it => (it === line
      ? { ...it, inventory_id: earlier.id, batch: earlier.batch_number, expiry: earlier.expiry_date, available: earlier.quantity_available, mrp: parseFloat(String(earlier.mrp)) }
      : it)));
  };

  // Pre-filled from the medicine's own master gst_percent, but staff can
  // override per sale.
  const updateItemGst = (idx: number, value: string) =>
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, gst: parseFloat(value) || 0 } : it));

  // If the admin removes a percentage currently selected on an item already
  // in the cart, fall back to the first configured option.
  useEffect(() => {
    setItems(prev => reconcileGstOptions(prev, gstPercentageOptions));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gstPercentageOptions]);

  // Live check against the cart while it's being built, before checkout —
  // same debounced call and the same endpoint DispensingTab.tsx's own
  // "Substitute / Add to cart" flow already makes. ADVISORY ONLY: never wired
  // into "Dispense & bill"'s disabled prop, matching SafetyAlertBanner's own
  // stated rule. A failed check is silent, not a blocker — the pharmacist
  // can still dispense with the request pending or the network down. This
  // screen's cart can never carry a substitution_reason (stockFor only ever
  // resolves a same-drug batch), so unlike DispensingTab.tsx's own use of
  // this hook, the mismatches half of the result is simply unused here.
  const { alerts: safetyAlerts } = useDispensingSafetyCheck(
    patient?.id,
    items,
    items.map(i => `${i.inventory_id}:${i.prescription_item_id ?? ''}`).join(',')
  );

  const dispense = async () => {
    if (items.length === 0) return;
    if (needsPrescriber && !prescriberName.trim()) { setError('Prescriber name is required — this includes a Schedule H1/X medicine.'); return; }
    if (unresolvedQuantityOverages.length) { setError('One or more items exceed the prescribed quantity — add a reason before dispensing.'); return; }
    if (unresolvedOffPrescription.length) { setError('One or more items aren’t on this patient’s prescription — add a reason before dispensing.'); return; }
    if (unresolvedLater.length) { setError('One or more items use a later-expiry batch while an earlier one is available — add a reason or switch batch before dispensing.'); return; }
    setBilling(true); setError('');
    try {
      const bill = await createPharmacyBill({
        patient_id: patient?.id,
        consultation_id: consultationId,
        visit_id: visit.id,
        payment_mode: payMode,
        payment_status: payStatus,
        prescriber_name: prescriberName.trim() || undefined,
        prescriber_reg_no: prescriberReg.trim() || undefined,
        items: items.map(i => ({
          inventory_id: i.inventory_id, name: i.name, quantity: i.qty, gst_percent: gstEnabled ? i.gst : 0,
          prescription_item_id: i.prescription_item_id, substitution_reason: i.substitution_reason,
          quantity_override_reason: i.quantity_override_reason,
          off_prescription_reason: i.off_prescription_reason,
          batch_selection_reason: i.batch_selection_reason,
          batch_selection_remarks: i.batch_selection_remarks,
        })),
      });
      // This dispense went through the legacy api.ts call above, not the
      // useCreatePharmacyBill mutation DispensingTab.tsx uses — so its
      // automatic cache invalidation never runs. Mirror it here: without
      // this, the Pharmacy module's "Active prescriptions" list (and
      // inventory) can keep showing this prescription/stock as before for
      // up to staleTime (30s) or until something else happens to refetch it.
      queryClient.invalidateQueries({ queryKey: keys.pharmacy.inventory() });
      queryClient.invalidateQueries({ queryKey: keys.prescriptions.list({ status: 'active' }) });
      if (items.some(i => i.prescription_item_id)) {
        queryClient.invalidateQueries({ queryKey: branchKey('prescription') });
      }
      completeStage('pharmacy', { dispensed: true, bill_id: bill.id, bill_number: bill.bill_number, total_amount: bill.total_amount });
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    setBilling(false);
  };

  // Why nothing was dispensed, recorded next to the existing `dispensed` flag
  // in the same stage_data blob — no new table, column or endpoint, and
  // StageDataModal renders it into the visit's history automatically since it
  // lists every stage_data key. Pharmacy's pending list keys off `dispensed`
  // alone (routes/pharmacy.js), so nothing here changes which prescriptions
  // are pending; this is purely the audit trail for *why*.
  //
  // Two genuinely different situations, told apart by whether anything could
  // have been dispensed at all — a cart line, or a prescribed line matched to
  // stock, means the medicines were on the shelf:
  //
  //   nothing on the shelf → nobody made a decision, so asking would be a
  //     pointless click. Completes straight away, reason recorded
  //     automatically as 'nothing_to_dispense' (unchanged behaviour).
  //   medicines available  → someone chose not to dispense them, and only
  //     that person knows why. Prompt, and record what they actually say —
  //     never a value inferred from cart state.
  const couldHaveDispensed = items.length > 0 || rxLines.some(l => l.batch);

  const skip = () => {
    if (!couldHaveDispensed) {
      completeStage('pharmacy', { dispensed: false, dispense_reason: 'nothing_to_dispense' });
      return;
    }
    setReasonCode('');
    setReasonText('');
    setReasonOpen(true);
  };

  // 'other' keeps its free text in a separate key so the reason stays
  // machine-readable (groupable in a report) while the specifics survive.
  const otherNeedsText = reasonCode === 'other' && !reasonText.trim();
  const confirmSkip = () => {
    if (!reasonCode || otherNeedsText) return;
    setReasonOpen(false);
    completeStage('pharmacy', {
      dispensed: false,
      dispense_reason: reasonCode,
      ...(reasonCode === 'other' ? { dispense_reason_text: reasonText.trim() } : {}),
    });
  };

  if (inventory === null || !rxLoaded) {
    return <StageLayout variant="single"><div className="card fade-in" style={{ textAlign: 'center', padding: 40, color: '#6B7280' }}>Loading inventory…</div></StageLayout>;
  }

  return (
    <div className="fade-in flex flex-col gap-3.5">
    <StageLayout variant="single">
    <div className="card">
      <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>💊 Pharmacy</div>
      <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 20 }}>Dispense any prescribed medicines for {patient?.name || visit?.name}</div>

      {error && <div style={{ padding: '10px 14px', background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 'var(--radius)', color: '#991B1B', fontSize: 13, marginBottom: 16 }}>{error}</div>}

      {/* Read-only record of what the doctor prescribed for this visit.
          Stocked lines are already in the cart below; the rest are shown so
          the pharmacist can tell "nothing was prescribed" from "we don't
          hold it here" before choosing between the two exits. */}
      {rxLines.length > 0 && (
        <div style={{ marginBottom: 16, padding: 12, background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 'var(--radius)' }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 8 }}>
            📋 Prescribed this visit{rx?.prescriber_name ? ` — ${rx.prescriber_name}` : ''}
          </div>
          {rxLines.map(({ it, batch, mismatch, partial }, idx) => {
            // A prescribed line the cart already carries — via the direct
            // "+ Add" (batch.id) or via 🔀 Substitute (a different
            // inventory_id entirely, so batch.id alone can't detect it; see
            // DispensingTab.tsx's identical cartedPrescriptionItemIds). Not
            // recomputed as a hook since rxLines/items already are.
            const carted = items.some(i => i.prescription_item_id === it.id);
            const fullyDispensed = it.remaining_quantity === 0;
            // No batch resolved at all is the case DispensingTab.tsx calls
            // "not linked to inventory" (a free-text prescribed drug — see
            // that file's identical unresolvedMedicine) vs. genuinely out of
            // stock. stockFor only ever matches by drug identity, so a null
            // batch here means Substitute is the ONLY way this line can be
            // fulfilled at all — see this file's own header comment on why
            // that machinery previously had no picker to reach it through.
            const unresolvedMedicine = it.medicine_id == null;
            const substituting = substitutingFor === it.id;
            return (
            <div key={it.id ?? idx} style={{ padding: '6px 0', borderTop: idx ? '1px solid #F3F4F6' : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span>{it.drug_name}{it.strength ? ` ${it.strength}` : ''}</span>
                  {mismatch && <span style={MISMATCH_BADGE}>⚠ Strength mismatch</span>}
                  {partial && <span style={MISMATCH_BADGE}>⚠ Partial stock</span>}
                </div>
                {/* Both numbers side by side, so the decision to keep or remove
                    the substitute needs no cross-referencing. */}
                {mismatch && batch && (
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#92400E' }}>
                    Prescribed: {it.strength} • Available: {batch.strength}
                  </div>
                )}
                {/* Dispensing still proceeds with what's on the shelf — lineFor
                    already clamps the cart quantity to it — this only makes
                    the shortfall visible so the pharmacist can choose to
                    accept it or remove the line instead. */}
                {partial && batch && (
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#92400E' }}>
                    {batch.quantity_available} available of {prescribedQty(it)} prescribed
                  </div>
                )}
                {doseSummary(it) && <div style={{ fontSize: 11, color: 'var(--text-2)' }}>{doseSummary(it)}</div>}
              </div>
              {fullyDispensed ? (
                <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 8, background: '#DCFCE7', color: '#166534' }}>✓ Already dispensed</span>
              ) : carted ? (
                <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 8, background: '#DCFCE7', color: '#166534' }}>✓ Added</span>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {batch && (
                    <button
                      className="btn-ghost" style={{ fontSize: 11, padding: '3px 8px' }}
                      onClick={() => addItem(batch.id, it.remaining_quantity != null ? Math.min(prescribedQty(it), it.remaining_quantity) : prescribedQty(it), it.id)}
                    >
                      + Add
                    </button>
                  )}
                  <button
                    className="btn-ghost" style={{ fontSize: 11, padding: '3px 8px' }}
                    onClick={() => setSubstitutingFor(substituting ? null : it.id)}
                  >
                    {batch ? '🔀 Substitute' : unresolvedMedicine ? '🔀 Not linked to inventory — pick a batch' : '🔀 Not in stock — substitute'}
                  </button>
                </div>
              )}
              </div>
              {substituting && (
                <SubstituteFlow
                  dispensable={dispensable}
                  defaultQty={it.remaining_quantity != null ? Math.min(prescribedQty(it), it.remaining_quantity) : prescribedQty(it)}
                  unresolvedMedicine={unresolvedMedicine}
                  prescriptionItemId={it.id}
                  onCancel={() => setSubstitutingFor(null)}
                  onDispense={(invId, qty, reason) => {
                    addItem(invId, qty, it.id, reason);
                    setSubstitutingFor(null);
                  }}
                />
              )}
            </div>
            );
          })}
        </div>
      )}
      {rxLoaded && !rx && (
        <div style={{ marginBottom: 16, fontSize: 12, color: 'var(--text-2)' }}>
          No structured prescription was recorded for this visit.
        </div>
      )}

      <div style={{ marginBottom: 8 }}>
        <label className="lbl">Search and Add medicine (FEFO batch — earliest expiry first)</label>
        <input
          type="text"
          placeholder="Type at least 3 characters…"
          value={medicineSearch}
          onChange={e => setMedicineSearch(e.target.value)}
        />
        {searchActive && (
          <div style={{ marginTop: 6, border: '1px solid #E5E7EB', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
            {searchResults.length === 0 ? (
              <div style={{ padding: '8px 10px', fontSize: 12, color: 'var(--text-2)' }}>No additional medicines found</div>
            ) : (
              searchResults.map((m, idx) => (
                <div
                  key={m.medicine_id}
                  onClick={() => pickSearchResult(m.id)}
                  style={{ padding: '7px 10px', cursor: 'pointer', borderTop: idx ? '1px solid #F3F4F6' : 'none' }}
                >
                  <div style={{ fontSize: 12.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                    {m.generic_name} {m.strength}
                    {m.schedule !== 'OTC' && <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 8, background: '#FEF3C7', color: '#92400E' }}>{m.schedule}</span>}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-2)' }}>
                    batch {m.batch_number}, exp {new Date(m.expiry_date).toLocaleDateString('en-IN')} ({totalsByMed[m.medicine_id]} total)
                    {isNearExpiry(m.expiry_date, nearExpiryDays) && ' · ⏳ near expiry'}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Temporarily hidden — the search-and-add box above replaces this as
          the primary way to add a medicine. Left in place, not deleted, so
          it can be restored by simply uncommenting this block.
      <div style={{ marginBottom: 8 }}>
        <label className="lbl">Add medicine (FEFO batch — earliest expiry first)</label>
        <select onChange={e => { if (e.target.value) { addItem(e.target.value); e.target.value = ''; } }} defaultValue="">
          <option value="">Select medicine…</option>
          {Object.values(fefoGroups).map(m => (
            <option key={m.medicine_id} value={m.id}>
              {m.generic_name} {m.strength} {m.schedule !== 'OTC' ? `[${m.schedule}]` : ''} — batch {m.batch_number}, exp {new Date(m.expiry_date).toLocaleDateString('en-IN')} ({totalsByMed[m.medicine_id]} total)
            </option>
          ))}
        </select>
      </div>
      */}
      <button className="btn-ghost" style={{ fontSize: 11, padding: '3px 8px', marginBottom: 10 }} onClick={() => setAdvancedBatch(a => !a)}>
        {advancedBatch ? '▲ Hide' : '▼'} Advanced: pick a specific batch
      </button>
      {advancedBatch && (
        <div style={{ marginBottom: 14 }}>
          <select onChange={e => { if (e.target.value) { addItem(e.target.value); e.target.value = ''; } }} defaultValue="">
            <option value="">Select exact batch…</option>
            {dispensable.map(m => (
              <option key={m.id} value={m.id}>{isNearExpiry(m.expiry_date, nearExpiryDays) ? '⏳ ' : ''}{m.generic_name} {m.strength} — {m.batch_number} (exp {new Date(m.expiry_date).toLocaleDateString('en-IN')}, {m.quantity_available} left)</option>
            ))}
          </select>
        </div>
      )}

      {items.map((item, idx) => (
        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #F3F4F6' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              {item.name}
              {item.schedule !== 'OTC' && <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 8, background: '#FEF3C7', color: '#92400E' }}>{item.schedule}</span>}
              {mismatchedInventoryIds.has(item.inventory_id) && <span style={MISMATCH_BADGE}>⚠ Strength mismatch</span>}
              {partialStockInventoryIds.has(item.inventory_id) && <span style={MISMATCH_BADGE}>⚠ Partial stock</span>}
              {item.substitution_reason && <span style={MISMATCH_BADGE} title={`Substitution: ${item.substitution_reason}`}>🔀 Substituted</span>}
              {item.quantity_override_reason && <span style={MISMATCH_BADGE} title={`Exceeds prescribed quantity: ${item.quantity_override_reason}`}>⚠ Over quantity</span>}
              <NearExpiryBadge expiry={item.expiry} nearExpiryDays={nearExpiryDays} />
              {item.batch_selection_reason && <span style={MISMATCH_BADGE} title={`Later-expiry batch: ${batchSelectionSummary(item, batchReasons)}`}>↷ Later batch</span>}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-2)' }}>
              Batch: {item.batch} · Exp: {item.expiry ? new Date(item.expiry).toLocaleDateString('en-IN') : '—'}
              {item.substitution_reason && <> · Reason: {item.substitution_reason}</>}
              {item.quantity_override_reason && <> · Reason: {item.quantity_override_reason}</>}
              {item.batch_selection_reason && <> · Batch reason: {batchSelectionSummary(item, batchReasons)}</>}
            </div>
          </div>
          <input type="number" style={{ width: 56 }} value={item.qty} min={1} max={item.available}
            onChange={e => setItems(prev => prev.map((it, i) => (
              // A quantity edit invalidates any quantity_override_reason
              // already applied to this line — it must be re-confirmed via
              // the banner below against the new amount.
              i === idx ? { ...it, qty: Math.min(parseInt(e.target.value) || 1, it.available), quantity_override_reason: undefined } : it
            )))} />
          {gstEnabled && (
            <div style={{ display:'flex', alignItems:'center', gap:4 }}>
              <span style={{ fontSize:10, fontWeight:600, color:'#9CA3AF' }}>GST</span>
              <select value={item.gst} onChange={e => updateItemGst(idx, e.target.value)} style={{ width: 65, fontSize: 12 }} title="GST % for this line">
                {gstPercentageOptions.map(g => <option key={g} value={g}>{g}%</option>)}
              </select>
            </div>
          )}
          <div style={{ fontSize: 12, fontWeight: 600, minWidth: 56, textAlign: 'right' }}>₹{(item.qty * item.mrp).toFixed(2)}</div>
          <button className="btn-danger" onClick={() => setItems(prev => prev.filter((_, i) => i !== idx))}>✕</button>
        </div>
      ))}

      <LaterBatchPanel
        className="mt-3"
        issues={unresolvedLater}
        reasons={batchReasons}
        onApply={applyLaterBatchReason}
        onUseEarlier={switchToEarlierBatch}
      />

      <SafetyAlertBanner alerts={safetyAlerts} className="mt-3" />

      {items.length > 0 && (
        <div style={{ marginTop: 14, padding: 12, background: '#F9FAFB', borderRadius: 'var(--radius)' }}>
          {(gstEnabled ? [['Subtotal', '₹' + subtotal.toFixed(2)], ['GST', '₹' + gstAmt.toFixed(2)], ['Total', '₹' + total.toFixed(2)]] : [['Subtotal', '₹' + subtotal.toFixed(2)], ['Total', '₹' + total.toFixed(2)]]).map(([l, v], i, arr) => (
            <div key={l} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: i < arr.length - 1 ? 6 : 0, fontWeight: i === arr.length - 1 ? 700 : 400, fontSize: i === arr.length - 1 ? 15 : 13, borderTop: i === arr.length - 1 ? '1px solid #E5E7EB' : 'none', paddingTop: i === arr.length - 1 ? 8 : 0 }}>
              {l}<span>{v}</span>
            </div>
          ))}

          {needsPrescriber && (
            <div style={{ marginTop: 12, padding: 10, background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 'var(--radius)' }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: '#92400E', marginBottom: 8 }}>⚠ Schedule H1/X item(s) — statutory register requires:</div>
              <div className="grid2">
                <div><label className="lbl">Prescriber name *</label><input value={prescriberName} onChange={e => setPrescriberName(e.target.value)} /></div>
                <div><label className="lbl">Prescriber reg. no.</label><input value={prescriberReg} onChange={e => setPrescriberReg(e.target.value)} /></div>
              </div>
            </div>
          )}

          {unresolvedQuantityOverages.length > 0 && (
            <div style={{ marginTop: 12, padding: 10, background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 'var(--radius)' }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: '#991B1B', marginBottom: 6 }}>⚠ Exceeds the prescribed quantity</div>
              {unresolvedQuantityOverages.map(i => {
                const remaining = remainingByRxItemId.get(i.prescription_item_id!);
                return (
                  <div key={i.inventory_id} style={{ fontSize: 12, color: '#991B1B', marginBottom: 4 }}>
                    Dispensing <b>{i.qty}</b> of <b>{i.name}</b>, but only <b>{remaining}</b> remain on the prescription.
                  </div>
                );
              })}
              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                <input
                  style={{ flex: 1 }}
                  placeholder="Reason for exceeding the prescribed quantity (e.g. fixed strip size)"
                  value={quantityOverrideReason}
                  onChange={e => setQuantityOverrideReason(e.target.value)}
                />
                <button className="btn-primary" style={{ fontSize: 12, padding: '4px 12px' }} disabled={!quantityOverrideReason.trim()} onClick={applyQuantityOverrideReason}>
                  Apply
                </button>
              </div>
            </div>
          )}

          {unresolvedOffPrescription.length > 0 && (
            <div style={{ marginTop: 12, padding: 10, background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 'var(--radius)' }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: '#991B1B', marginBottom: 6 }}>⚠ Not on this patient's prescription</div>
              {unresolvedOffPrescription.map(i => (
                <div key={i.inventory_id} style={{ fontSize: 12, color: '#991B1B', marginBottom: 4 }}>
                  <b>{i.name}</b> was added outside the doctor's prescribed list.
                  {i.schedule !== 'OTC' && <> This is a Schedule {i.schedule} medicine.</>} Remove it or add a reason before dispensing.
                </div>
              ))}
              {offPrescriptionMayBeSubstitution && (
                <div style={{ fontSize: 11.5, color: '#92400E', background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 'var(--radius)', padding: 6, marginBottom: 6 }}>
                  ⚠ There's still an unfulfilled prescribed line below — if this is meant to replace it, remove this
                  line and use that line's own 🔀 Substitute button instead, so it's linked, counted against the
                  prescription, and tracked for reporting. Otherwise a reason here is fine.
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                <input
                  style={{ flex: 1 }}
                  placeholder="Reason for dispensing outside the prescription (e.g. doctor verbally approved, OTC add-on)"
                  value={offPrescriptionReason}
                  onChange={e => setOffPrescriptionReason(e.target.value)}
                />
                <button className="btn-primary" style={{ fontSize: 12, padding: '4px 12px' }} disabled={!offPrescriptionReason.trim()} onClick={applyOffPrescriptionReason}>
                  Apply
                </button>
              </div>
            </div>
          )}

          <div className="grid2" style={{ marginTop: 12 }}>
            <div>
              <label className="lbl">Payment mode</label>
              <select value={payMode} onChange={e => setPayMode(e.target.value as PaymentMode)}>
                {(['Cash', 'UPI', 'Card'] as const).map(m => <option key={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="lbl">Payment status</label>
              <select value={payStatus} onChange={e => setPayStatus(e.target.value as PaymentStatus)}>
                <option value="Paid">Paid now</option>
                <option value="Pending">Pending</option>
              </select>
            </div>
          </div>
        </div>
      )}

    </div>
    </StageLayout>
    <StageActionBar
      readiness={
        unresolvedQuantityOverages.length > 0 ? 'Resolve the quantity overage to continue'
          : unresolvedOffPrescription.length > 0 ? 'Resolve the off-prescription item(s) to continue'
          : unresolvedLater.length > 0 ? 'Resolve the later-expiry batch choice to continue'
          : items.length === 0 ? 'Nothing added to the cart yet'
          : '✓ Ready to dispense'
      }
      readinessTone={unresolvedQuantityOverages.length > 0 || unresolvedOffPrescription.length > 0 || unresolvedLater.length > 0 ? 'warn' : items.length === 0 ? 'neutral' : 'success'}
    >
      <button className="btn-ghost" disabled={loading || billing} onClick={skip}>
        Nothing to dispense — skip →
      </button>
      <button className="btn-primary" disabled={items.length === 0 || loading || billing || unresolvedQuantityOverages.length > 0 || unresolvedOffPrescription.length > 0 || unresolvedLater.length > 0} onClick={dispense}>
        {billing ? 'Dispensing…'
          : unresolvedQuantityOverages.length > 0 ? 'Resolve quantity to continue'
          : unresolvedOffPrescription.length > 0 ? 'Resolve off-prescription item(s) to continue'
          : unresolvedLater.length > 0 ? 'Resolve batch choice to continue'
          : 'Dispense & bill →'}
      </button>
    </StageActionBar>

      {/* Skip-reason prompt. Same overlay treatment as StageDataModal (the
          modal this workflow already uses) so it needs no new visual
          language. Dismissing it — backdrop click or Cancel — leaves the
          stage exactly as it was: nothing is recorded and nothing advances,
          so the pharmacist can go back and dispense after all. */}
      {reasonOpen && (
        <div onClick={() => setReasonOpen(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(17,24,39,.45)', zIndex: 200,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '60px 20px', overflowY: 'auto',
        }}>
          <div onClick={e => e.stopPropagation()} className="card" style={{ maxWidth: 420, width: '100%', padding: 0 }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #E5E7EB', fontSize: 15, fontWeight: 700 }}>
              Why was the medicine not dispensed?
            </div>

            <div style={{ padding: '14px 20px', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {DISPENSE_REASONS.map(r => (
                <label key={r.value} style={{
                  display: 'flex', alignItems: 'center', gap: 9, padding: '7px 8px', borderRadius: 'var(--radius)',
                  fontSize: 13, cursor: 'pointer', background: reasonCode === r.value ? '#F3F4F6' : 'transparent',
                }}>
                  <input
                    type="radio" name="dispense_reason" value={r.value}
                    checked={reasonCode === r.value}
                    onChange={() => setReasonCode(r.value)}
                    style={{ width: 'auto', margin: 0 }}
                  />
                  <span>{r.label}</span>
                </label>
              ))}

              {reasonCode === 'other' && (
                <input
                  autoFocus
                  value={reasonText}
                  onChange={e => setReasonText(e.target.value)}
                  placeholder="Short reason (required)"
                  maxLength={200}
                  style={{ marginTop: 8 }}
                />
              )}
            </div>

            <div style={{ display: 'flex', gap: 10, padding: '0 20px 16px' }}>
              <button className="btn-ghost" style={{ flex: 1 }} onClick={() => setReasonOpen(false)}>Cancel</button>
              <button
                className="btn-primary" style={{ flex: 1 }}
                disabled={!reasonCode || otherNeedsText || loading}
                onClick={confirmSkip}
              >
                Confirm skip →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
